// src/storage/spellingStorage.ts — Local-First Persistence for Spelling Reef
// Persists error words, diagnostic attempts, and streak baselines in localStorage
// with forward-compatibility for IndexedDB and Cloudflare D1 sync.

import type { SpellingAttempt, SpellingTier, SpellingActivity } from '../ui/components/spellingReef';

const STORAGE_KEY = 'clawey_spelling_progress_v1';

export interface SpellingProgressData {
  errorLog: SpellingAttempt[];
  streakCount: number;
  bestStreak: number;
  masteredWords: string[];
  currentTier: SpellingTier;
  currentActivity: SpellingActivity;
  totalTimeMs: number;
  lastSessionDate: string;
}

function getDefaultProgress(): SpellingProgressData {
  return {
    errorLog: [],
    streakCount: 0,
    bestStreak: 0,
    masteredWords: [],
    currentTier: 'shallows',
    currentActivity: 'diagnostic',
    totalTimeMs: 0,
    lastSessionDate: new Date().toISOString().split('T')[0] ?? '',
  };
}

export class SpellingStorage {
  /** Load saved progress or default */
  static load(): SpellingProgressData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return getDefaultProgress();
      const parsed = JSON.parse(raw) as Partial<SpellingProgressData>;
      return {
        ...getDefaultProgress(),
        ...parsed,
        errorLog: Array.isArray(parsed.errorLog) ? [...parsed.errorLog] : [],
        masteredWords: Array.isArray(parsed.masteredWords) ? [...parsed.masteredWords] : [],
      };
    } catch (err) {
      console.warn('Failed to load spelling progress from storage:', err);
      return getDefaultProgress();
    }
  }

  /** Save current progress */
  static save(data: SpellingProgressData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save spelling progress to storage:', err);
    }
  }

  /** Mark word as mastered */
  static markWordMastered(word: string): void {
    const data = this.load();
    if (!data.masteredWords.includes(word.toLowerCase())) {
      data.masteredWords.push(word.toLowerCase());
      this.save(data);
    }
  }

  /** Record an attempt and update streaks */
  static recordAttempt(attempt: SpellingAttempt): SpellingProgressData {
    const data = this.load();
    data.errorLog.push(attempt);

    if (attempt.correct) {
      data.streakCount += 1;
      if (data.streakCount > data.bestStreak) {
        data.bestStreak = data.streakCount;
      }
      if (!data.masteredWords.includes(attempt.word.toLowerCase())) {
        data.masteredWords.push(attempt.word.toLowerCase());
      }
    } else {
      data.streakCount = 0;
    }

    this.save(data);
    return data;
  }

  /** Reset all progress (for fresh testing/parent restart) */
  static reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors in private browsing
    }
  }
}
